import {
  faCommentDots,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { memo, useContext, useMemo } from 'react';
import { DealCommentsContext } from '~/contexts/deal-comments';
import { useDiscussionStats } from '~/hooks/useDiscussionStats';
import { useUserStatus } from '~/hooks/userStatus';
import { DEAL_COMMENT_TYPE } from '~/lib/discussions/constants';
import { CommentV2 } from '~/lib/discussions/types';
import { PDPContext } from '~/lib/product/context';
import { Deal } from '~/types/deal';

interface CommentModeratorInfoProps {
  useNewUI?: boolean;
  type: DEAL_COMMENT_TYPE;
}

function CommentModeratorInfo({
  useNewUI,
  type,
}: Readonly<CommentModeratorInfoProps>) {
  const {
    deal: { id: dealId },
  }: { deal: Deal } = useContext(PDPContext);
  const { userStatus } = useUserStatus(dealId);
  const { discussionStats } = useDiscussionStats(dealId, type);
  const { items: comments } = useContext(DealCommentsContext);

  const { needs_moderation: needsModeration, needs_reply: needsReply } =
    useMemo(() => discussionStats || {}, [discussionStats]);

  const statusCounts = useMemo(() => {
    let unresolved = 0;
    let pending = 0;

    const traverseComments = (comments: CommentV2[]) => {
      comments.forEach((comment: CommentV2) => {
        if (!comment.resolved) {
          unresolved++;
        }

        if (comment.approved === null) {
          pending++;
        }

        if (comment.children?.length) {
          traverseComments(comment?.children);
        }
      });
    };

    traverseComments(comments);

    return {
      unresolved,
      pending,
    };
  }, [comments]);

  // Old UI
  if (!useNewUI) {
    return (
      <div>
        <span className="text-sm">
          Unresolved: <strong>{statusCounts.unresolved}</strong>
        </span>
        <span className="ml-3 text-sm">
          Pending Moderation: <strong>{statusCounts.pending}</strong>
        </span>
      </div>
    );
  }

  return (
    <div className="flex">
      {!!needsReply && (
        <div>
          <FontAwesomeIcon
            icon={faCommentDots}
            className="mr-1 text-base text-brick"
          />
          <span className="text-sm">
            {needsReply} needs{' '}
            {type === DEAL_COMMENT_TYPE.REVIEW ? 'reply' : 'answer'}
          </span>
        </div>
      )}

      {!!needsReply && userStatus?.staff_moderator && !!needsModeration && (
        <span className="mx-2 text-slate">|</span>
      )}

      {userStatus?.staff_moderator && !!needsModeration && (
        <div>
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className="mr-1 text-base text-dorado"
          />
          <span className="text-sm">{needsModeration} needs moderation</span>
        </div>
      )}
    </div>
  );
}

export default memo(CommentModeratorInfo);
